import { Box, TextField } from '@mui/material'
import { EvseConfig, EvsePort, PortNumber } from '../../types/ports'
import { PortControl } from './PortControl'
import { Trans } from 'react-i18next'
import { ListSelector } from '../../components/atoms/ListSelector'
import { Cable, EvStation, EvStationOutlined, Lan } from '@mui/icons-material'
import { ReactNode } from 'react'
import { ID } from '../../types/models'
import { OptimizerControlPortControl } from './OptimizerControlPortControl'

/**
 * Maps the EVSE info `status` byte (see spp_evse.h SPP_EVSE_INFO_STATUS_*) to an i18n key.
 */
const EVSE_STATUS_KEYS: Record<number, string> = {
  0: 'EvseStatus-UNKNOWN',
  1: 'EvseStatus-IDLE',
  2: 'EvseStatus-INITIATING',
  3: 'EvseStatus-CHARGING',
  4: 'EvseStatus-SUSPENDED',
  5: 'EvseStatus-COMPLETED',
  6: 'EvseStatus-DISABLED',
  7: 'EvseStatus-FAULT',
  8: 'EvseStatus-COMM_FAIL',
}

export function driverDefaults(driver: string) {
  switch (driver) {
    case 'EVSE_DRIVER_GENERIC':
      return {
        onoff_exists: false,
        limit_exists: false,
        onoff_invert: false,
        limit_invert: false,
        onoff_port: '0/0' as PortNumber,
        limit_port: '0/0' as PortNumber,
        nonlimited_current: 10,
        limited_current: 10
      }
    case 'EVSE_DRIVER_CHARLIE_RS485':
      return {
        rs485_inst: 0,
        slave_id: 1,
        max_current: 10,
      }
    case 'EVSE_DRIVER_SUNGROW_MODBUS':
      return {
        modbus_inst: 0,
        slave_id: 0,
        max_current: 10,
      }
    // Every field of driver_foxess_modbus must be listed: the device read-back returns them all
    // and the pending config is compared key-by-key against it.
    case 'EVSE_DRIVER_FOXESS_MODBUS':
      return {
        conn_type: 0,
        slave_id: 1,
        max_current: 10,
        baud_115200: false,
        ctrl_current: false,
        no_validity_write: false,
        force_ip_last_octet: 0,
        tcp_port: 0,
        heartbeat_s: 0,
        validity_s: 0,
      }
    default:
      throw new Error(`Default config for EVSE driver ${driver} not implemented.`)
  }
}

/**
 * Controller for Electric Vehicle charger.
 */
export class EVSEPortControl extends PortControl<EvsePort, EvseConfig> {

  getDescription(): string { return this.app.t('Electric vehicle charger') }

  getTypeName(): string { return this.app.t('PortType-EVSE') }

  isOn(): boolean {
    return !!this.getValue('configured_current_limit')
  }

  getConnectionStatus(): string {
    const status = this.getValue('status') as number
    const key = EVSE_STATUS_KEYS[status] ?? 'EvseStatus-UNKNOWN'
    return this.app.t(key)
  }

  isOff(): boolean {
    return !this.isOn()
  }

  turnOn(): void {
    this.sendCommand({ name: 'current_limit', value: null })
  }

  turnOff(): void {
    this.sendCommand({ name: 'current_limit', value: 0 })
  }

  getConfigDefaults(): EvseConfig {
    return {
      enabled: false,
      driver: 'EVSE_DRIVER_CHARLIE_RS485',
      params: driverDefaults('EVSE_DRIVER_CHARLIE_RS485'),
    }
  }

  /**
   * Find the corresponding optimizer control.
   */
  optimizerControl(): OptimizerControlPortControl | null {
    const ctrl = this.deviceControl.findPortBy(portControl =>
      portControl.port.type === 'OPTIMIZER_CONTROL' &&
      (portControl as OptimizerControlPortControl).getConfig('port') === this.port.port_number)
    return ctrl
      ? ctrl as OptimizerControlPortControl
      : null
  }

  getAssociatedPortIDs(): ID[] {
    const opt = this.optimizerControl()
    if (!opt) {
      throw new Error(`Cannot find optimizer control for port #${this.port.id}.`)
    }
    return [this.port.id as ID, opt.port.id as ID]
  }

  configTopLeft(): React.ReactNode {
    const driver = this.getConfig('driver') as string

    const onChangeDriver = (driver: string) => {
      this.setConfig('enabled', true)
      this.setConfig('driver', driver)

      const newParams = driverDefaults(driver)

      this.setConfig('params', newParams)
    }

    return (
      <Box>
        <Trans>Charger Model</Trans>
        <ListSelector
          items={{
            EVSE_DRIVER_CHARLIE_RS485: 'Innohome Charlie',
            EVSE_DRIVER_SUNGROW_MODBUS: 'Sungrow EVSE',
            EVSE_DRIVER_FOXESS_MODBUS: 'FoxESS / 1KOMMA5° EVSE',
          }}
          icons={[EvStation, EvStationOutlined, EvStation, EvStationOutlined]}
          selected={driver || null}
          onSelect={onChangeDriver}
        />
      </Box>
    )
  }

  configTopRight(): ReactNode {
    const driver = this.getConfig('driver') as string

    const onChangeParam = (param: string, value: any) => {
      this.setConfig('params', { ...this.getConfig('params'), [param]: value })
    }

    const params: Record<string, unknown> = this.getConfig('params')

    if (driver === 'EVSE_DRIVER_GENERIC') return <>Not implemented</>

    if (driver === 'EVSE_DRIVER_CHARLIE_RS485') {

      const old = this.saveState('max_current', params.max_current || 10)

      return <Box>
        <Trans>Connection</Trans>
        <ListSelector
          items={{
            0:
              this.app.t('ConnType-RS485') +
              ' ' + this.app.t('Port {number}').replace('{number}', '0'),
            1:
              this.app.t('ConnType-RS485') +
              ' ' + this.app.t('Port {number}').replace('{number}', '1'),
          }}
          icons={[
            Cable,
            Cable,
          ]}
          selected={`${params.rs485_inst || '0'}`}
          onSelect={(e) => onChangeParam('rs485_inst', parseInt(e))}
        />
        <TextField
          margin="normal"
          label={this.app.t('Slave ID')}
          data-cy="text-Slave ID EV Charger"
          size="small"
          value={params.slave_id}
          type="number"
          onChange={(e) => onChangeParam('slave_id', e.target.value)}
          onBlur={(e) => onChangeParam('slave_id', parseInt(e.target.value || '0'))}
        />
        <TextField
          margin="normal"
          label={this.app.t('Maximum charge current')}
          data-cy="text-Maximum charge current"
          size="small"
          value={params.max_current}
          type="number"
          onChange={(e) => onChangeParam('max_current', e.target.value)}
          onBlur={(e) => onChangeParam('max_current', parseInt(e.target.value || '0'))}
        />
        {!!params.max_current && old !== params.max_current && <Box sx={{ color: 'error.light' }}>
            <Trans>WARNING: Before increasing the value, ask the electrician if the fuse and wiring can handle the current.</Trans>
          </Box>
        }
      </Box>
    }

    if (driver === 'EVSE_DRIVER_SUNGROW_MODBUS') {

      const old = this.saveState('max_current', params.max_current || 10)

      return <Box>
        <Trans>Connection</Trans>
        <ListSelector
          items={{
            0:
              this.app.t('ConnType-RS485') +
              ' ' + this.app.t('Port {number}').replace('{number}', '0'),
            1:
              this.app.t('ConnType-RS485') +
              ' ' + this.app.t('Port {number}').replace('{number}', '1'),
          }}
          icons={[
            Cable,
            Cable,
          ]}
          selected={`${params.modbus_inst || '0'}`}
          onSelect={(e) => onChangeParam('modbus_inst', parseInt(e))}
        />
        <TextField
          margin="normal"
          label={this.app.t('Slave ID')}
          data-cy="text-Slave ID EV Charger"
          size="small"
          value={params.slave_id}
          type="number"
          onChange={(e) => onChangeParam('slave_id', e.target.value)}
          onBlur={(e) => onChangeParam('slave_id', parseInt(e.target.value || '0'))}
        />
        <Box sx={{ fontSize: '85%', opacity: 0.8 }}>
          <Trans>0 = AUTO</Trans>
        </Box>
        <TextField
          margin="normal"
          label={this.app.t('Maximum charge current')}
          data-cy="text-Maximum charge current"
          size="small"
          value={params.max_current}
          type="number"
          onChange={(e) => onChangeParam('max_current', e.target.value)}
          onBlur={(e) => onChangeParam('max_current', parseInt(e.target.value || '0'))}
        />
        {!!params.max_current && old !== params.max_current && <Box sx={{ color: 'error.light' }}>
            <Trans>WARNING: Before increasing the value, ask the electrician if the fuse and wiring can handle the current.</Trans>
          </Box>
        }
      </Box>
    }

    if (driver === 'EVSE_DRIVER_FOXESS_MODBUS') {

      const old = this.saveState('max_current', params.max_current || 10)

      return <Box>
        <Trans>Connection</Trans>
        <ListSelector
          items={{
            0:
              this.app.t('ConnType-RS485') +
              ' ' + this.app.t('Port {number}').replace('{number}', '0'),
            1:
              this.app.t('ConnType-RS485') +
              ' ' + this.app.t('Port {number}').replace('{number}', '1'),
            2: String(this.app.t('Modbus TCP')),
          }}
          icons={[
            Cable,
            Cable,
            Lan,
          ]}
          selected={`${params.conn_type || '0'}`}
          onSelect={(e) => onChangeParam('conn_type', parseInt(e))}
        />
        <TextField
          margin="normal"
          label={this.app.t('Slave ID')}
          data-cy="text-Slave ID EV Charger"
          size="small"
          value={params.slave_id}
          type="number"
          onChange={(e) => onChangeParam('slave_id', e.target.value)}
          onBlur={(e) => onChangeParam('slave_id', parseInt(e.target.value || '0'))}
        />
        <TextField
          margin="normal"
          label={this.app.t('Maximum charge current')}
          data-cy="text-Maximum charge current"
          size="small"
          value={params.max_current}
          type="number"
          onChange={(e) => onChangeParam('max_current', e.target.value)}
          onBlur={(e) => onChangeParam('max_current', parseInt(e.target.value || '0'))}
        />
        {!!params.max_current && old !== params.max_current && <Box sx={{ color: 'error.light' }}>
            <Trans>WARNING: Before increasing the value, ask the electrician if the fuse and wiring can handle the current.</Trans>
          </Box>
        }
      </Box>
    }

    return <></>
  }
}
